import type {
  PositionBudget,
  PositionBudgetSummary,
  RosterSettings,
  ScholarshipModel,
  ScholarshipPlayer,
  ScholarshipPlayerPosition,
} from '../../../../types/scholarships';
import { DepthChartCard } from './DepthChartCard';
import { DEPTH_CHART_CARDS, fieldRowCapacity, type CardPlayersMap } from './depthChartPositions';
import { LIST_CARD_ORDER } from './depthChartLayout';
import type { DepthChartView } from './scholarshipFilterStorage';
import { positionLabel } from './positionGroups';
import type { DepthChartDisplayMode } from './scholarshipPercent';

interface Props {
  sport: 'baseball' | 'softball';
  playersByCard: CardPlayersMap;
  scholarshipModel: ScholarshipModel;
  displayMode: DepthChartDisplayMode;
  positionBudgets: PositionBudgetSummary[];
  /** The configured needs (players wanted + note) per position, for each card's need line. */
  positionNeeds?: PositionBudget[];
  settings: RosterSettings;
  onClickPlayer: (player: ScholarshipPlayer) => void;
  /** Omitted: the cards are read-only. */
  onReorder?: (position: ScholarshipPlayerPosition, orderedIds: string[]) => void;
  /** The scale the cards are drawn at (field layout only), so a dragged row follows the pointer. */
  zoom?: number;
  /** 'list' drops the field and lays the same cards out in a grid. */
  view?: DepthChartView;
  /** The field is drawn at its design size and scaled to fit (see depthChart.css). */
  fitted?: boolean;
}

/**
 * Baseball field — green outfield arc, a kite-shaped skinned-dirt area
 * around the basepaths, and a smaller green "infield grass" diamond visible
 * in the middle (between the bases). The mound sits on the dirt, just behind
 * the infield grass.
 *
 * Layer order matters: grass arc → dirt → infield grass diamond → mound → bases
 * → foul lines → home plate. Same 1000x800 viewBox as softball so the card
 * anchors in DEPTH_CHART_CARDS stay stable.
 */
const BaseballField = () => (
  <svg
    className="depth-chart-diamond__field"
    viewBox="0 0 1000 800"
    preserveAspectRatio="xMidYMid meet"
    aria-hidden="true"
    focusable="false"
  >
    {/* Outfield grass — fills the upper portion of the field with an arched
        top, so the LF/CF/RF cards sit on grass instead of empty background. */}
    <path
      d="M 0 720 L 0 100 Q 500 -100 1000 100 L 1000 720 Z"
      className="depth-chart-diamond__grass"
    />

    {/* Skinned-dirt infield — kite shape from home up to 1B, arcing around the
        back of 2B, down to 3B, and back to home. Subsumes the basepath strips. */}
    <path
      d="M 440 720 L 250 520 Q 500 220 750 520 L 560 720 Z"
      className="depth-chart-diamond__dirt"
    />

    {/* Infield grass diamond — sized as large as will fit inside the dirt kite
        (top is just below the dirt arc's peak at y≈370). Centered on (500, 540)
        so the mound sits at its exact midpoint. */}
    <path
      d="M 500 400 L 670 540 L 500 680 L 330 540 Z"
      className="depth-chart-diamond__infield-grass"
    />

    {/* Pitcher's mound — sits at the geometric center of the infield grass. */}
    <circle cx="500" cy="540" r="22" className="depth-chart-diamond__mound" />

    {/* Bases at 1B and 3B — small white squares rotated 45°. (2B omitted —
        the dirt kite's peak doesn't reach the geometric 2B position, so a base
        rendered there would float in the outfield grass.) */}
    <rect
      x="743"
      y="513"
      width="14"
      height="14"
      transform="rotate(45 750 520)"
      className="depth-chart-diamond__base"
    />
    <rect
      x="243"
      y="513"
      width="14"
      height="14"
      transform="rotate(45 250 520)"
      className="depth-chart-diamond__base"
    />

    {/* Foul lines — collinear with the dirt kite's home-side edges so the
        white lines read as a seamless extension of the basepaths into the
        outfield. Slope ≈ 1.053 (rise/run from the home corner through the
        base out to the viewBox edge). */}
    <line
      x1="440"
      y1="720"
      x2="0"
      y2="257"
      className="depth-chart-diamond__foul-line"
    />
    <line
      x1="560"
      y1="720"
      x2="1000"
      y2="257"
      className="depth-chart-diamond__foul-line"
    />

    {/* Home plate — pentagon. */}
    <polygon
      points="488,712 512,712 518,725 500,738 482,725"
      className="depth-chart-diamond__plate"
    />
  </svg>
);

/**
 * Softball field — identical to baseball except there's no infield grass
 * diamond. The entire skinned-dirt infield reads as solid brown, which is the
 * headline visual difference between a softball and a baseball field.
 */
const SoftballField = () => (
  <svg
    className="depth-chart-diamond__field"
    viewBox="0 0 1000 800"
    preserveAspectRatio="xMidYMid meet"
    aria-hidden="true"
    focusable="false"
  >
    {/* Outfield grass — fills the upper portion of the field. */}
    <path
      d="M 0 720 L 0 100 Q 500 -100 1000 100 L 1000 720 Z"
      className="depth-chart-diamond__grass"
    />

    {/* Skinned-dirt infield — same kite shape as baseball. */}
    <path
      d="M 440 720 L 250 520 Q 500 220 750 520 L 560 720 Z"
      className="depth-chart-diamond__dirt"
    />

    {/* Pitcher's mound — same position/size as baseball. */}
    <circle cx="500" cy="540" r="22" className="depth-chart-diamond__mound" />

    {/* Bases at 1B, 2B, 3B. */}
    <rect
      x="743"
      y="513"
      width="14"
      height="14"
      transform="rotate(45 750 520)"
      className="depth-chart-diamond__base"
    />
    <rect
      x="243"
      y="513"
      width="14"
      height="14"
      transform="rotate(45 250 520)"
      className="depth-chart-diamond__base"
    />

    {/* Foul lines — collinear with the dirt kite's home-side edges so the
        white lines read as a seamless extension of the basepaths into the
        outfield. Slope ≈ 1.053 (rise/run from the home corner through the
        base out to the viewBox edge). */}
    <line
      x1="440"
      y1="720"
      x2="0"
      y2="257"
      className="depth-chart-diamond__foul-line"
    />
    <line
      x1="560"
      y1="720"
      x2="1000"
      y2="257"
      className="depth-chart-diamond__foul-line"
    />

    {/* Home plate — pentagon. */}
    <polygon
      points="488,712 512,712 518,725 500,738 482,725"
      className="depth-chart-diamond__plate"
    />
  </svg>
);

export const DepthChartDiamond = ({
  sport,
  playersByCard,
  scholarshipModel,
  displayMode,
  positionBudgets,
  positionNeeds = [],
  settings,
  onClickPlayer,
  onReorder,
  zoom,
  view = 'field',
  fitted = false,
}: Props) => {
  const isList = view === 'list';
  const cards = isList
    ? LIST_CARD_ORDER.flatMap((id) => DEPTH_CHART_CARDS.filter((c) => c.id === id))
    : DEPTH_CHART_CARDS;
  // The `rosters.depth.drag` tour anchor goes on the first card, in the order the cards render
  // (the field's, or the list's), that can sort.
  const dragAnchorCard = onReorder
    ? cards.find(
        (card) => playersByCard[card.id].filter((a) => a.appearance === 'primary').length > 1,
      )?.id
    : undefined;
  return (
  <div
    className={`depth-chart-diamond depth-chart-diamond--${sport}${isList ? ' depth-chart-diamond--list' : fitted ? ' depth-chart-diamond--fitted' : ''}`}
    role="region"
    aria-label={
      isList
        ? 'Depth chart by position'
        : `${sport === 'softball' ? 'Softball' : 'Baseball'} depth chart diamond`
    }
  >
    {!isList && (sport === 'softball' ? <SoftballField /> : <BaseballField />)}
    <div className="depth-chart-diamond__cards">
      {cards.map((card) => (
        <DepthChartCard
          key={card.id}
          spec={card}
          label={positionLabel(card.id, sport)}
          appearances={playersByCard[card.id]}
          scholarshipModel={scholarshipModel}
          displayMode={displayMode}
          positionBudget={positionBudgets.find((b) => b.position === card.id)}
          need={positionNeeds.find((b) => b.position === card.reorderPosition)}
          settings={settings}
          onClickPlayer={onClickPlayer}
          onReorder={onReorder}
          zoom={zoom}
          ownsDragAnchor={card.id === dragAnchorCard}
          positioned={!isList}
          rowCap={!isList && fitted ? fieldRowCapacity(card) : undefined}
        />
      ))}
    </div>
  </div>
  );
};
